import { useCallback, useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import { BigButton } from '../buttons/bigButton'
import { RequestVarName, RequestVarNameType } from '../../blocks/variables'
import { useTranslator } from '../translator/hook'
import '../../styles/modal.css'
import classNames from 'classnames'

const RESERVED_WORDS = [
  'and',
  'for',
  'or',
  'break',
  'function',
  'repeat',
  'do',
  'if',
  'return',
  'else',
  'in',
  'then',
  'elseif',
  'local',
  'true',
  'end',
  'nil',
  'until',
  'false',
  'not',
  'while',
  'event',
  'Instance',
  'script',
  'onPlay',
  'Vector3',
  'CFrame',
  'print',
  'BrickColor',
  'Enum',
  'onTouch',
  'hit',
  'humanoid',
  'game',
  'onMessage',
  'click',
  'time',
  'Random',
]
const IDENTIFIER_CHAR_REGEX = /^[0-9a-zA-Z_]*$/
const IDENTIFIER_START_REGEX = /^[a-zA-Z_]/

export type VariableModalProps = {
  event: RequestVarName | null
  onClose: () => void
}

export const VariableModal = ({ event, onClose }: VariableModalProps) => {
  Modal.setAppElement('#root')
  const { t } = useTranslator()
  const [name, setName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    const name = event?.variable?.name ?? ''
    setName(name)
    if (event) setTimeout(() => inputRef.current?.select(), 50)
  }, [event])

  const handleClose = useCallback(() => {
    setName('')
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(() => {
    if (name !== event?.variable?.name) event?.callback(name)
    handleClose()
  }, [event, name, handleClose])

  useEffect(() => {
    if (RESERVED_WORDS.includes(name)) {
      setErrorMessage(t('variable_name_reserved'))
    } else if (
      event?.workspace.getVariable(name) !== null &&
      name != event?.variable?.name
    ) {
      setErrorMessage(t('variable_name_in_use', [name]))
    } else if (!IDENTIFIER_CHAR_REGEX.test(name)) {
      setErrorMessage(t('variable_name_invalid_char'))
    } else if (name.startsWith('_')) {
      setErrorMessage(t('variable_name_invalid_start_underscore'))
    } else if (name && !IDENTIFIER_START_REGEX.test(name)) {
      setErrorMessage(t('variable_name_invalid_start'))
    } else {
      setErrorMessage('')
    }
  }, [event, name, t])

  const disableSubmit = !name || !!errorMessage

  return (
    <Modal
      className="modal-content"
      isOpen={!!event}
      contentLabel="Example Modal"
      onRequestClose={onClose}
      style={{
        overlay: {
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          zIndex: 861,
        },
      }}
    >
      <div className="modal-detail">
        {event?.variable?.name ?
          t('RENAME_VARIABLE_TITLE', [event?.variable?.name])
        : t('enter_variable_name')}
      </div>
      <div className="modal-input-container">
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) =>
            e.key === 'Enter' && !disableSubmit && handleSubmit()
          }
          className="modal-input"
        />
        {errorMessage && <div className="modal-error">{errorMessage}</div>}
      </div>
      <div>
        <BigButton
          className={classNames('modal-action-big-button', {
            'disabled-big-button': disableSubmit,
          })}
          disabled={disableSubmit}
          onClick={handleSubmit}
        >
          {event?.type === RequestVarNameType.CREATE ?
            t('B_MAKE_NEW_VARIABLE')
          : t('rename_variable')}
        </BigButton>
        <BigButton onClick={onClose}>{t('cancel_button')}</BigButton>
      </div>
    </Modal>
  )
}
